import * as Sentry from '@sentry/nextjs'
import dayjs from 'dayjs'
import Cookies from 'js-cookie'

import { FBC_COOKIE_NAME } from '@/legacy-src/analytics/FbclidCapture'
import getGa4PriceInEur from '@/legacy-src/analytics/getGa4PriceInEur'
import { type UserType } from '@/legacy-src/types/UserStateType'
import { hashText } from '@/legacy-src/utilities/hash'

const IS_FB_CAPI_ENABLED = process.env.NEXT_PUBLIC_FB_CAPI_ENABLED === 'true'

export enum FbEventName {
  DiagnoseStartTotal = 'DiagnoseStartTotal',
  DiagnoseFinishTotal = 'DiagnoseFinishTotal',
  CompleteRegistration = 'CompleteRegistration',
  Purchase = 'Purchase',
  PageView = 'PageView',
  PurchaseAcquisition = 'PurchaseAcquisition',
}

// get rid of '0' or '+' sign at the start of the string
const normalizePhone = (phone: string) => phone.replace(/^(?:\+|0*)(\d+)$/, '$1')

const getBaseUserData = async (user) => {
  return {
    fbc: Cookies.get(FBC_COOKIE_NAME),
    fbp: Cookies.get('_fbp'),
    em: [await hashText(user.email?.toLowerCase())],
    fn: [await hashText(user.firstName?.toLowerCase())],
    ln: [await hashText(user.lastName?.toLowerCase())],
    ct: user.city ? [await hashText(user.city?.toLowerCase())] : undefined,
    zp: user.zip ? [await hashText(user.zip?.toLowerCase())] : undefined,
    country: user.residenceCountryCode ? [await hashText(user.residenceCountryCode.toLowerCase())] : undefined,
    client_user_agent: window.navigator.userAgent,
    client_ip_address: user.userIp !== 'undefined' ? user.userIp : undefined,
    external_id: user.id,
  }
}

const sendEvent = async (data) => {
  if (!IS_FB_CAPI_ENABLED) {
    return
  }

  try {
    await fetch('/api/capi', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        data,
      }),
    })
  } catch (e) {
    // /api/capi reports its own failures — this only catches a request that never reached it
    // (ad blocker, offline), which is the one failure mode invisible server-side.
    Sentry.captureException(e, { tags: { type: 'fbCapi sendEvent' } })
  }
}

type PurchaseFbEventType = {
  event: { price: string; id: number; therapistId: number }
  user: UserType & { userIp: string }
  isAcquisition?: boolean
}

const sendPurchaseFbEvent = async ({ event, user, isAcquisition }: PurchaseFbEventType) => {
  if (process.env.NEXT_PUBLIC_ENV !== 'test') {
    const phone = user.phone ? normalizePhone(user.phone) : undefined
    const value = getGa4PriceInEur(event.price, process.env.NEXT_PUBLIC_CURRENCY)

    const dataToSend = [
      {
        event_name: isAcquisition ? FbEventName.PurchaseAcquisition : FbEventName.Purchase,
        event_id: `${event.id}`,
        event_time: dayjs().unix(),
        user_data: {
          ...(await getBaseUserData(user)),
          ph: phone ? [await hashText(phone)] : undefined,
        },
        custom_data: {
          currency: 'EUR',
          value: value,
          content_type: 'product',
          contents: [
            {
              id: event.therapistId,
              item_price: value,
              quantity: 1,
            },
          ],
        },
        event_source_url: window.location.href,
        action_source: 'website',
      },
    ]

    if (window.fbq) {
      window.fbq(
        'track',
        isAcquisition ? FbEventName.PurchaseAcquisition : FbEventName.Purchase,
        { value, currency: 'EUR' },
        { eventID: `${event.id}` },
      )
    }

    sendEvent(dataToSend)
  }
}

type EventParams = {
  eventName: string
  user: any
  customData?: any
}

const sendFbEventByName = async ({ eventName, user, customData }: EventParams) => {
  if (process.env.NEXT_PUBLIC_ENV !== 'test') {
    const dataToSend = [
      {
        event_name: eventName,
        event_time: dayjs().unix(),
        user_data: await getBaseUserData(user),
        custom_data: customData,
      },
    ]

    sendEvent(dataToSend)
  }
}

const sendFbTherapistEventByName = async ({ eventName, user, therapistData }) => {
  if (process.env.NEXT_PUBLIC_ENV !== 'test') {
    const dataToSend = [
      {
        event_name: eventName,
        event_time: dayjs().unix(),
        user_data: await getBaseUserData(user),
        custom_data: {
          currency: 'EUR',
          item_name: `${therapistData.firstName} ${therapistData.lastName}`,
          item_id: therapistData.therapistId,
        },
        event_source_url: window.location.href,
        action_source: 'website',
      },
    ]

    sendEvent(dataToSend)
  }
}

export { sendPurchaseFbEvent, sendFbEventByName, sendFbTherapistEventByName }
